import { isNil } from 'lodash';
import { useCallback, useRef, useState } from 'react';
import { LayoutAnimation } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { Menu } from '@shared/components/Menu';
import { View } from '@shared/components/View';
import { MBHover } from '@shared/components/hover';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ReactionButtonWithPicker } from '@shared/scenes/chat/thread/components/reaction/ReactionButtonWithPicker';
import { MBInvisibleReactionPicker } from '@shared/scenes/chat/thread/containers/messageInvisibleReactionPicker';
import { MBMessageReactionsAndSeenBy } from '@shared/scenes/chat/thread/containers/messageReactionsAndSeenBy';
import { getAnchorName } from '@shared/styles';
import { API } from '@shared/types/api';
import { MBAlert } from '@shared/util/alert';
import { MBAnalytics } from '@shared/util/analytics';
import { copyToClipboard } from '@shared/util/clipboard';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { openLink } from '@shared/util/link';
import { MBMessageDeliveryStatus } from '../deliveryStatus';
import { MBUrgentBadge } from '../urgentBadge';
import { MBMessageBubbleAttachments } from './components/attachments';
import { MBMessageBubbleContent } from './components/content';
import { MBSenderImage } from './components/senderImage';
import { MBSenderName } from './components/senderName';
import { MBSentBySMS } from './components/sentBySMS';
import { Styles } from './styles';

export interface MBMessageBubbleProps {
  message: API.ChatMessageFragment;
  layout: MBChatMessageLayoutOptions;
  theme: MBMessageBubbleTheme;
  deliveryStatus?: MBDeliveryStatus;

  /*
        Used by reaction buttons etc to determine if should call mutation
        for channel message or thread comment.
        TODO(fant): remove this when chat messages have been unified.
    */
  channelSlug: string;

  /*
        Callback for when message bubble is pressed. If this is not set,
        the default press action will be showing message details (aka time sent)
    */
  onPress?: VoidFunction;

  /*
        Callback for when the user wants to remove the message. When this
        prop is sent "Remove Message" will be shown on long press. So this
        property should not be set when the user cannot remove (eg for
        messages sent by other users).
    */
  onDelete?: VoidFunction;

  /*
        Callback for when the user wants to edit the message
    */
  onEdit?: VoidFunction;

  /*
        TODO(fant): when searching and selecting a message, we need to scroll
        to it in a chat thread. This does not work well together with link
        previews since they're loaded async and push around the entire content.
        For now, we solve this by simply hiding link previews when showing
        search results.
    */
  isHidingLinkPreview?: boolean;

  isDesktop: boolean;
  marginLeft?: number;
}

export function MBMessageBubble({
  message,
  layout,
  theme,
  deliveryStatus,
  channelSlug,
  onPress: onPressProp,
  onDelete,
  onEdit,
  isHidingLinkPreview,
  isDesktop,
}: MBMessageBubbleProps) {
  const [isShowingDetails, setIsShowingDetails] = useState(false);
  const [isHovered, onHoverStart, onHoverEnd] = useBoolean(!isDesktop);
  const [isLongPressMenuOpen, setIsLongPressMenuOpen] = useState(false);
  const [longPressMenuUrl, setLongPressMenuUrl] = useState<string | null>(null);
  const reactionPickerRef = useRef<ReactionButtonWithPicker | null>(null);
  const anchorName = getAnchorName('message-bubble', message.id);

  const isSent = !deliveryStatus || deliveryStatus === 'sent';

  const isShowingSenderName = isShowingDetails || (!layout.isGroupedWithAbove && isSent);

  const toggleDetails = useCallback(() => {
    const willShowDetails = !isShowingDetails;
    setIsShowingDetails(willShowDetails);
    if (!!LayoutAnimation) {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }
    if (willShowDetails) {
      MBAnalytics.track('Message::ShowDetails', { messageId: message.id });
    }
  }, [isShowingDetails, message.id]);

  const confirmDelete = useCallback(
    () =>
      MBAlert.confirm({
        title: 'Delete message',
        message: 'Are you sure you want to delete this message? This action cannot be undone.',
        confirmText: 'Yes, delete',
        confirmStyle: 'destructive',
        onConfirm: onDelete,
      }),
    [onDelete],
  );

  const closeLongPressMenu = useCallback(() => {
    setIsLongPressMenuOpen(false);
    setLongPressMenuUrl(null);
  }, []);

  const onLongPressMenuOpenChange = useCallback((open: boolean) => {
    setIsLongPressMenuOpen(open);
    if (!open) {
      setLongPressMenuUrl(null);
    }
  }, []);

  const onPress = useCallback(() => {
    if (isLongPressMenuOpen) {
      closeLongPressMenu();
      return;
    }
    if (onPressProp) {
      onPressProp();
    } else {
      toggleDetails();
    }
  }, [closeLongPressMenu, isLongPressMenuOpen, onPressProp, toggleDetails]);

  const onLongPress = useCallback(() => {
    const urlToken = message.meta.tokens.find(token => token?.type === 'url');
    setLongPressMenuUrl(urlToken?.value ?? null);
    setIsLongPressMenuOpen(true);
  }, [message.meta.tokens]);

  const addReaction = useCallback(() => {
    reactionPickerRef.current?.showPicker();
  }, []);

  const copyText = useCallback(() => {
    copyToClipboard(message.content);
    MBAlert.alert({ message: i18n('common.copied') });
  }, [message.content]);

  const showEditMessage = message.sender.isMe && !!message.content.length && !!onEdit;
  const showDeleteMessage = !!onDelete && message.sender.isMe;
  const isLinkPreviewMenu = !isNil(longPressMenuUrl);
  const showEditInMenu = isLinkPreviewMenu ? !!onEdit : showEditMessage;
  const showDeleteInMenu = isLinkPreviewMenu ? !!onDelete : showDeleteMessage;
  const menuAlign = theme.align === 'left' ? 'left' : 'right';

  const content = (
    <>
      {/*
          This is somewhat of a hack that needs explaining... To easily show the
          reaction picker when pressing "Add Reaction..." on message long press,
          we want to reuse our picker logic from reaction/buttonWithPicker to
          not have to copy code. To easily do that, we have this thin component
          that is basically an invisible MBReactionButtonWithPicker. When we
          want to add a reaction from an MBActionSheet, we simply get the ref
          of this component and call showPicker. Somewhat hacky, but fairly clean.
        */}
      <MBInvisibleReactionPicker
        ref={ref => {
          reactionPickerRef.current = ref;
        }}
        anchorName={anchorName}
        message={message}
        channelSlug={channelSlug}
      />

      {isShowingSenderName && (
        <MBSenderName
          name={message.sender.isMe ? undefined : message.sender.fullName}
          align={theme.align}
          imagePlaceholder={theme.showImage}
          date={message.createdAt}
        />
      )}

      {/* render the content containing bubble and attachment */}
      <MBView
        style={[
          theme.align === 'left' && Styles.rowAlignLeft,
          theme.align === 'right' && Styles.rowAlignRight,
        ]}
      >
        {theme.showImage && <MBSenderImage message={message} layout={layout} />}

        <View fill>
          {/* 1. at the very top, render the urgent badge */}
          {!!message.urgent && <MBUrgentBadge align={theme.align} />}

          {/* 2. then render the message bubble and its content */}
          <MBHover {...{ onHoverStart, onHoverEnd }}>
            <MBMessageBubbleContent
              theme={theme}
              message={message}
              layout={layout}
              channelSlug={channelSlug}
              isHidingLinkPreview={isHidingLinkPreview}
              hovering={isHovered}
              isFirstMessageInGroup={isShowingSenderName}
              anchorName={anchorName}
            />
          </MBHover>

          {/* 3. all attachments in a long list */}
          <MBMessageBubbleAttachments
            message={message}
            align={theme.align}
            channelSlug={channelSlug}
            isSent={isSent}
            onLongPress={onLongPress}
            blink={!!theme.blinkBackgroundColor}
            isFirstMessageInGroup={isShowingSenderName}
          />
        </View>
      </MBView>

      {/*
                    Note: the following elements are rendered below the actual message content.
                    Otherwise the sender image would be pushed up to not be bottom aligned with
                    the message bubble.
                */}
      <View row>
        {/*
                        Add a placeholder for the image so that left aligned content
                        (eg sent by) is aligned with the message bubble
                    */}
        {theme.showImage && <MBView style={Styles.userImagePlaceholder} />}

        <MBView style={[Styles.flex, theme.align === 'right' && Styles.alignRight]}>
          {/* 4. if the message has been sent by SMS or as urgent, show that */}
          {isSent && <MBSentBySMS message={message} />}

          {/*
                            5. a row of reactions and the seen by indicator
                            Note: hiding seen by in grouped messages will
                            make the user miss some seen bys
                        */}
          <MBMessageReactionsAndSeenBy
            message={message}
            layout={layout}
            align={theme.align}
            isDM={theme.isDM}
            channelSlug={channelSlug}
          />
        </MBView>
      </View>

      {/*
                    Lastly, if we have a delivery status (only relevant for
                    the blue me bubble), render that status.
                    Note: don't show this if the message is already seen by someone
                    Note: hiding seen by in grouped messages will make
                        the user miss some seen bys
                */}
      {!!deliveryStatus && !layout.seenByUsers.length && (
        <MBMessageDeliveryStatus
          status={deliveryStatus}
          isEdited={!!message.editedAt}
          style={Styles.deliveryStatus}
          testID="chat-message-delivered"
        />
      )}
    </>
  );

  const longPressMenu = (
    <Menu
      isOpen={isLongPressMenuOpen}
      onOpenChange={onLongPressMenuOpenChange}
      anchorName={anchorName}
      align={menuAlign}
      lightDismiss={false}
    >
      <Menu.Content>
        {!isNil(longPressMenuUrl) && (
          <Menu.Item
            label={i18n('chat.message.longPressMenu.visitLink')}
            onClick={() => openLink(longPressMenuUrl)}
          />
        )}
        {isSent && (
          <Menu.Item label={i18n('chat.message.longPressMenu.addReaction')} onClick={addReaction} />
        )}
        {!isLinkPreviewMenu && !!message.content && (
          <Menu.Item label={i18n('chat.message.longPressMenu.copyText')} onClick={copyText} />
        )}
        {showEditInMenu && (
          <Menu.Item label={i18n('chat.message.longPressMenu.editMessage')} onClick={onEdit} />
        )}
        {showDeleteInMenu && (
          <>
            <Menu.Separator />
            <Menu.Item
              label={i18n('chat.message.longPressMenu.deleteMessage')}
              danger
              onClick={() => {
                confirmDelete();
              }}
            />
          </>
        )}
      </Menu.Content>
    </Menu>
  );

  if (isDesktop) {
    return <>{content}</>;
  }

  return (
    <>
      <MBTouchableOpacity activeOpacity={0.7} onPress={onPress} onLongPress={onLongPress}>
        {content}
      </MBTouchableOpacity>
      {longPressMenu}
    </>
  );
}
